import React, { useCallback, useContext, useState } from "react"
import { useInterval, useMount } from "ahooks"
import classNames from "classnames"
import { noop } from "lodash-es"

import Logo from "@/components/Logo"
import MainLoader from "@/components/MainLoader"
import { useTranslation } from "@/locales"
import Menu from "../../components/Menu"
import { GlobalContext, IPageProps } from "../../context"
import loadAssets from "../../utils/resource"

import cloud1Png from "../../assets/img/cloud/1.png"
import cloud1Webp from "../../assets/img/cloud/1.webp"
import cloud2Png from "../../assets/img/cloud/2.png"
import cloud2Webp from "../../assets/img/cloud/2.webp"
import cloud3Png from "../../assets/img/cloud/3.png"
import cloud3Webp from "../../assets/img/cloud/3.webp"
import cloud4Png from "../../assets/img/cloud/4.png"
import cloud4Webp from "../../assets/img/cloud/4.webp"
import cloud5Png from "../../assets/img/cloud/5.png"
import cloud5Webp from "../../assets/img/cloud/5.webp"
import cloud6Png from "../../assets/img/cloud/6.png"
import cloud6Webp from "../../assets/img/cloud/6.webp"
import styles from "./index.module.less"

const clouds = [
    {
        webp: cloud2Webp,
        png: cloud2Png,
    },
    {
        webp: cloud1Webp,
        png: cloud1Png,
    },
    {
        webp: cloud3Webp,
        png: cloud3Png,
    },
    {
        webp: cloud5Webp,
        png: cloud5Png,
    },
    {
        webp: cloud4Webp,
        png: cloud4Png,
    },
    {
        webp: cloud6Webp,
        png: cloud6Png,
    },
]

const PageLoading: React.FC<IPageProps> = (props) => {
    const { className, style, onNext = noop } = props

    const { dispatch } = useContext(GlobalContext)
    const { t } = useTranslation()

    const [percent, setPercent] = useState(0)
    const [hide, setHide] = useState(true)

    const init = useCallback(async () => {
        // 加载资源
        loadAssets()

        // 监听资源加载
        const loader = MainLoader.getInst()
        loader.startLoadInitial()
        loader.addEventListener("progress", (e) => {
            const event = e as ProgressEvent
            const progress = event.loaded / event.total
            const percent = Math.min(Number((progress * 100).toFixed(0)), 100)
            setPercent(percent)
            if (percent >= 100) {
                dispatch({
                    type: "updateContext",
                    payload: { assertLoaded: true },
                })
            }
        })
    }, [dispatch])

    useInterval(() => {
        setHide(true)
        window.setTimeout(() => setHide(false), 2000)
    }, 9000)

    const handleClick = useCallback(async () => {
        if (percent >= 100) {
            onNext()
        }
    }, [onNext, percent])

    useMount(() => {
        init()
        setHide(false)
    })

    return (
        <div
            className={classNames(styles.pageLoading, className)}
            style={style}
            onClick={handleClick}
        >
            <div className={styles.title}>
                <div className={classNames(styles.lotus, hide && styles.hide)}>
                    <div className={styles.stem} />
                    <div className={styles.backPetal}>
                        {Array.from({ length: 4 }).map((_, index) => (
                            <div key={index} className={styles.petal} />
                        ))}
                    </div>
                    <div className={styles.pod} />
                    <div className={styles.frontPetal}>
                        {Array.from({ length: 4 }).map((_, index) => (
                            <div key={index} className={styles.petal} />
                        ))}
                    </div>
                </div>
                <div className={styles.en} />
            </div>
            <div className={styles.loading}>
                <div className={styles.percentage}>
                    <div className={styles.smoke} />
                    <div className={styles.gate}>
                        <div
                            className={styles.sun}
                            style={{
                                opacity: percent / 100,
                                transform: `translateY(${50 - (percent / 100) * 50}px)`,
                            }}
                        />
                    </div>
                    <div className={styles.banner}>
                        <div
                            className={classNames(
                                styles.value,
                                percent < 100 && styles.show
                            )}
                        >{`${percent}%`}</div>
                        <div
                            className={classNames(
                                styles.value,
                                percent >= 100 && styles.show
                            )}
                        >
                            {t("enter")}
                        </div>
                    </div>
                </div>
                <div className={styles.progressBar}>
                    <div className={styles.bar}>
                        <div
                            className={styles.fill}
                            style={{
                                transform: `translateX(${-100 + percent}%)`,
                            }}
                        />
                    </div>
                </div>
                <div className={styles.bottom} />
            </div>
            <div className={styles.cloud}>
                {clouds.map((cloud, index) => (
                    <picture key={index}>
                        <source srcSet={cloud.webp} type="image/webp" />
                        <img src={cloud.png} alt="cloud" />
                    </picture>
                ))}
            </div>
            <Logo
                safeArea={false}
                className={styles.logo}
                returnClassName={styles.return}
            />
            <Menu />
        </div>
    )
}

export default PageLoading
